import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { formatDate } from "@/lib/format";
import { Plus, Ban, Calendar as CalIcon, AlertTriangle, Edit3, Trash2, ShieldOff, X } from "lucide-react";
import { toast } from "sonner";

const RESTRICTION_META = {
    stop_sell: { label: "STOP · shitja e mbyllur", short: "S", color: "bg-red-100 text-red-800 border-red-200", pill: "bg-red-600" },
    closed_to_arrival: { label: "CTA · nuk pranon check-in", short: "A", color: "bg-amber-100 text-amber-800 border-amber-200", pill: "bg-amber-500" },
    closed_to_departure: { label: "CTD · nuk pranon check-out", short: "D", color: "bg-purple-100 text-purple-800 border-purple-200", pill: "bg-purple-600" },
    min_stay: { label: "Min stay", pill: "bg-slate-700" },
};

const EMPTY_RULE = {
    name: "",
    room_type: "",
    date_from: "",
    date_to: "",
    min_stay: 1,
    max_stay: 0,
    stop_sell: false,
    closed_to_arrival: false,
    closed_to_departure: false,
    active: true,
};

export default function Restrictions() {
    const [rules, setRules] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null); // null | rule
    const [form, setForm] = useState(EMPTY_RULE);

    const roomTypes = useMemo(() => {
        const s = new Set();
        for (const r of rooms) if (r.room_type) s.add(r.room_type);
        return Array.from(s).sort();
    }, [rooms]);

    const load = async () => {
        setLoading(true);
        try {
            const [r, rms] = await Promise.all([
                api.get("/revenue/stay-rules"),
                api.get("/rooms"),
            ]);
            setRules(r.data || []);
            setRooms(rms.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ngarkim dështoi");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(EMPTY_RULE); setShowForm(true); };
    const openEdit = (rule) => {
        setEditing(rule);
        setForm({
            name: rule.name || "",
            room_type: rule.room_type || "",
            date_from: rule.date_from || "",
            date_to: rule.date_to || "",
            min_stay: rule.min_stay || 1,
            max_stay: rule.max_stay || 0,
            stop_sell: !!rule.stop_sell,
            closed_to_arrival: !!rule.closed_to_arrival,
            closed_to_departure: !!rule.closed_to_departure,
            active: rule.active !== false,
        });
        setShowForm(true);
    };

    const save = async () => {
        if (!form.date_from || !form.date_to) {
            toast.error("Data e fillimit dhe mbarimit janë të detyrueshme");
            return;
        }
        if (form.date_to < form.date_from) {
            toast.error("Data e mbarimit duhet pas fillimit");
            return;
        }
        if (!form.stop_sell && !form.closed_to_arrival && !form.closed_to_departure &&
            (!form.min_stay || form.min_stay <= 1) && !form.max_stay) {
            toast.error("Zgjidh të paktën 1 restriksion (STOP / CTA / CTD / min-stay / max-stay)");
            return;
        }
        try {
            if (editing) {
                await api.patch(`/revenue/stay-rules/${editing.id}`, form);
                toast.success("Rregulli u përditësua");
            } else {
                await api.post("/revenue/stay-rules", form);
                toast.success("Rregulli u krijua");
            }
            setShowForm(false);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ruajtja dështoi");
        }
    };

    const remove = async (rule) => {
        if (!window.confirm(`Të fshihet "${rule.name}"?`)) return;
        try {
            await api.delete(`/revenue/stay-rules/${rule.id}`);
            toast.success("Rregulli u hoq");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fshirja dështoi");
        }
    };

    const toggleActive = async (rule) => {
        try {
            await api.patch(`/revenue/stay-rules/${rule.id}`, { active: !rule.active });
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const activeCount = rules.filter((r) => r.active).length;

    return (
        <>
            <Topbar
                title="Kufizime Rezervimesh"
                subtitle={`${activeCount} rregulla aktive · ${rules.length} gjithsej`}
                actions={
                    <button onClick={openNew} className="akm-btn-primary" data-testid="new-restriction-btn">
                        <Plus size={14} /> Rregull i ri
                    </button>
                }
            />
            <div className="flex-1 overflow-auto p-6">
                {/* Info banner */}
                <div className="mb-4 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-[13px] text-blue-900">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-blue-600" />
                    <div>
                        <div className="font-semibold">Rregullat për shitjen dhe qëndrimin</div>
                        <div className="mt-0.5 text-blue-800/90">
                            Konfiguroni kufizime për periudha të caktuara: mbyllja e shitjes ({" "}
                            <b>STOP</b>), refuzim check-in ({" "}<b>CTA</b>), refuzim check-out ({" "}<b>CTD</b>),
                            qëndrim minimal ose maksimal. Këto sinkronizohen automatikisht me Channel Manager
                            dhe shfaqen si badges në kalendar.
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">Duke ngarkuar…</div>
                ) : rules.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center">
                        <ShieldOff size={28} className="mx-auto mb-2 text-muted-foreground" />
                        <div className="text-sm font-semibold text-foreground">Asnjë rregull i konfiguruar</div>
                        <div className="mt-1 text-[12px] text-muted-foreground">Krijoni rregullin e parë për të kufizuar shitjen ose qëndrimin</div>
                        <button onClick={openNew} className="akm-btn-primary mt-4">
                            <Plus size={13} /> Krijo rregullin e parë
                        </button>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-xl border border-border bg-card">
                        <table className="w-full text-[13px]">
                            <thead className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2.5 text-left">Emri</th>
                                    <th className="px-4 py-2.5 text-left">Kategoria</th>
                                    <th className="px-4 py-2.5 text-left">Periudha</th>
                                    <th className="px-4 py-2.5 text-left">Restrictions</th>
                                    <th className="px-4 py-2.5 text-left">Qëndrim</th>
                                    <th className="px-4 py-2.5 text-center">Aktiv</th>
                                    <th className="px-4 py-2.5"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rules.map((r) => (
                                    <tr key={r.id} className={`transition-colors hover:bg-slate-50/60 ${!r.active ? "opacity-50" : ""}`} data-testid={`restriction-row-${r.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-foreground">{r.name || "—"}</div>
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground">
                                            {r.room_type || <span className="italic text-muted-foreground">Të gjitha</span>}
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground tabular-nums text-[12px]">
                                            {formatDate(r.date_from)} → {formatDate(r.date_to)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {r.stop_sell && (<span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${RESTRICTION_META.stop_sell.color}`}>STOP</span>)}
                                                {r.closed_to_arrival && (<span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${RESTRICTION_META.closed_to_arrival.color}`}>CTA</span>)}
                                                {r.closed_to_departure && (<span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${RESTRICTION_META.closed_to_departure.color}`}>CTD</span>)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-[12px] text-foreground">
                                            {(r.min_stay > 1 || r.max_stay > 0) ? (
                                                <span>
                                                    {r.min_stay > 1 && <>min <b>{r.min_stay}n</b></>}
                                                    {r.min_stay > 1 && r.max_stay > 0 && " · "}
                                                    {r.max_stay > 0 && <>max <b>{r.max_stay}n</b></>}
                                                </span>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => toggleActive(r)} className={`inline-flex h-5 w-9 items-center rounded-full transition ${r.active ? "justify-end bg-emerald-500" : "justify-start bg-slate-300"}`} data-testid={`restriction-toggle-${r.id}`}>
                                                <span className="mx-0.5 h-4 w-4 rounded-full bg-white shadow" />
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => openEdit(r)} className="mr-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Modifiko" data-testid={`restriction-edit-${r.id}`}>
                                                <Edit3 size={13} />
                                            </button>
                                            <button onClick={() => remove(r)} className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700" title="Fshi" data-testid={`restriction-delete-${r.id}`}>
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showForm && (
                <RestrictionDrawer
                    editing={editing}
                    form={form}
                    setForm={setForm}
                    roomTypes={roomTypes}
                    onClose={() => setShowForm(false)}
                    onSave={save}
                />
            )}
        </>
    );
}


function RestrictionDrawer({ editing, form, setForm, roomTypes, onClose, onSave }) {
    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[480px] flex-col bg-card shadow-xl" data-testid="restriction-drawer">
                <div className="flex h-14 items-center justify-between border-b border-border px-4">
                    <div>
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            <Ban size={14} className="text-[#ff7b02]" />
                            {editing ? "Modifiko rregullin" : "Krijo rregull të ri"}
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"><X size={14} /></button>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                    <Field label="Emri i rregullit">
                        <input className="akm-input" value={form.name} placeholder="p.sh. Verë 2026 - Suite"
                               onChange={(e) => setForm({ ...form, name: e.target.value })}
                               data-testid="restriction-name" />
                    </Field>

                    <Field label="Kategoria e dhomës">
                        <select className="akm-input" value={form.room_type}
                                onChange={(e) => setForm({ ...form, room_type: e.target.value })}
                                data-testid="restriction-room-type">
                            <option value="">— Të gjitha kategoritë —</option>
                            {roomTypes.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
                        </select>
                    </Field>

                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Nga data">
                            <input type="date" className="akm-input" value={form.date_from}
                                   onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                                   data-testid="restriction-from" />
                        </Field>
                        <Field label="Deri më">
                            <input type="date" className="akm-input" value={form.date_to}
                                   onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                                   data-testid="restriction-to" />
                        </Field>
                    </div>

                    <div className="pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Restrictions
                    </div>
                    <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-red-200 bg-red-50/40 p-3 hover:bg-red-50">
                        <input type="checkbox" checked={form.stop_sell}
                               onChange={(e) => setForm({ ...form, stop_sell: e.target.checked })}
                               className="mt-0.5 h-4 w-4 accent-red-600" data-testid="restriction-stop-sell" />
                        <div>
                            <div className="text-[13px] font-semibold text-red-800">STOP · Mbyll shitjen</div>
                            <div className="text-[11px] text-red-700">Nuk pranon rezervime të reja për këtë periudhë</div>
                        </div>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3 hover:bg-amber-50">
                            <input type="checkbox" checked={form.closed_to_arrival}
                                   onChange={(e) => setForm({ ...form, closed_to_arrival: e.target.checked })}
                                   className="mt-0.5 h-4 w-4 accent-amber-500" data-testid="restriction-cta" />
                            <div>
                                <div className="text-[12px] font-semibold text-amber-800">CTA</div>
                                <div className="text-[10.5px] text-amber-700">Nuk pranon check-in</div>
                            </div>
                        </label>
                        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-purple-200 bg-purple-50/40 p-3 hover:bg-purple-50">
                            <input type="checkbox" checked={form.closed_to_departure}
                                   onChange={(e) => setForm({ ...form, closed_to_departure: e.target.checked })}
                                   className="mt-0.5 h-4 w-4 accent-purple-600" data-testid="restriction-ctd" />
                            <div>
                                <div className="text-[12px] font-semibold text-purple-800">CTD</div>
                                <div className="text-[10.5px] text-purple-700">Nuk pranon check-out</div>
                            </div>
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-2">
                        <Field label="Qëndrim minimal (netë)">
                            <input type="number" min="1" className="akm-input" value={form.min_stay}
                                   onChange={(e) => setForm({ ...form, min_stay: Number(e.target.value) })}
                                   data-testid="restriction-min-stay" />
                        </Field>
                        <Field label="Qëndrim maksimal (0 = pa limit)">
                            <input type="number" min="0" className="akm-input" value={form.max_stay}
                                   onChange={(e) => setForm({ ...form, max_stay: Number(e.target.value) })}
                                   data-testid="restriction-max-stay" />
                        </Field>
                    </div>

                    <label className="mt-2 flex cursor-pointer items-center gap-2">
                        <input type="checkbox" checked={form.active}
                               onChange={(e) => setForm({ ...form, active: e.target.checked })}
                               className="h-4 w-4 accent-emerald-500" data-testid="restriction-active" />
                        <span className="text-[13px] font-semibold">Aktiv</span>
                    </label>
                </div>

                <div className="flex items-center gap-2 border-t border-border p-3">
                    <button onClick={onClose} className="akm-btn-ghost">Anulo</button>
                    <div className="flex-1" />
                    <button onClick={onSave} className="akm-btn-primary" data-testid="restriction-save">
                        {editing ? "Ruaj ndryshimet" : "Krijo rregullin"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}
